/* Nordic JD theme: cart drawer, bundle picker, toasts, header and product page behavior. */
(() => {
  const T = window.theme;
  const S = T.strings;
  const $ = (sel, el = document) => el.querySelector(sel);
  const $$ = (sel, el = document) => [...el.querySelectorAll(sel)];
  const fmt = (n) => n.toFixed(2);
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const icon = (name) => { const el = $(`#IconTpl`)?.content.querySelector(`[data-i="${name}"]`); return el ? el.innerHTML : ''; };
  const cartStrings = JSON.parse($('#CartStrings')?.textContent || '{}');
  // Liquid's t filter HTML-escapes strings; decode once so esc() doesn't double-escape.
  const decode = (str) => { const t = document.createElement('textarea'); t.innerHTML = str; return t.value; };
  [S, cartStrings].forEach((o) => Object.keys(o).forEach((k) => { o[k] = decode(o[k]); }));
  const tiers = T.tiers.map((t) => ({ ...t, qty: Number(t.qty), price: parseFloat(String(t.price).replace(',', '.')) })).sort((a, b) => b.qty - a.qty);

  /* ---------- Toasts (sonner look) ---------- */
  function toast(title, description, error) {
    const box = $('#Toasts');
    if (!box) return;
    const el = document.createElement('div');
    el.className = 'pointer-events-auto rounded-lg border bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-4 text-[13px] text-[#171717] transition-all duration-300 opacity-0 -translate-y-2';
    el.innerHTML = `<div class="font-medium ${error ? 'text-destructive' : ''}">${esc(title)}</div>${description ? `<div class="text-[#3f3f3f] mt-0.5">${esc(description)}</div>` : ''}`;
    box.appendChild(el);
    requestAnimationFrame(() => el.classList.remove('opacity-0', '-translate-y-2'));
    setTimeout(() => { el.classList.add('opacity-0'); setTimeout(() => el.remove(), 300); }, 4000);
  }

  /* ---------- Overlays ---------- */
  function openOverlay(el) { el.hidden = false; document.documentElement.style.overflow = 'hidden'; }
  function closeOverlay(el) { el.hidden = true; if (!$$('[data-overlay]').some((o) => !o.hidden)) document.documentElement.style.overflow = ''; }
  document.addEventListener('click', (e) => {
    const c = e.target.closest('[data-overlay-close]');
    if (c) closeOverlay(c.closest('[data-overlay]'));
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') $$('[data-overlay]').forEach((o) => !o.hidden && closeOverlay(o)); });

  /* ---------- Cart API ---------- */
  let cart = null;
  const api = async (url, body) => {
    const res = await fetch(url, { method: body ? 'POST' : 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: body ? JSON.stringify(body) : undefined });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || res.statusText);
    return res.json();
  };
  const fetchCart = async () => { cart = await api(`${T.routes.cart}.js`); renderCart(); return cart; };
  const addItems = async (items) => { await api(`${T.routes.cartAdd}.js`, { items }); return fetchCart(); };
  const changeLine = async (key, quantity) => { cart = await api(`${T.routes.cartChange}.js`, { id: key, quantity }); renderCart(); };
  const clearCart = () => api(`${T.routes.cartClear}.js`, {});

  const bagCount = () => (cart ? cart.items.reduce((n, i) => n + i.quantity, 0) : 0);
  const tierFor = (qty) => tiers.find((t) => qty >= t.qty) || null;
  const perBag = (qty) => { const t = tierFor(qty); return t ? t.price / t.qty : null; };

  function renderCart() {
    if (!cart) return;
    const count = bagCount();
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.classList.toggle('hidden', count === 0); });

    const bag = perBag(count);
    const total = cart.items.reduce((s, i) => s + (bag ?? i.price / 100) * i.quantity, 0);
    const threshold = Number(T.freeShippingThreshold) || 69;
    const remaining = Math.max(0, threshold - total);
    const pct = Math.min(100, (total / threshold) * 100);
    const currency = cart.currency;

    let html;
    if (cart.items.length === 0) {
      html = `<div class="flex-1 flex items-center justify-center py-16"><div class="text-center">${icon('cart')}<p class="text-muted-foreground">${esc(cartStrings.empty)}</p></div></div>`;
    } else {
      const items = cart.items.map((i) => {
        const unit = i.price / 100;
        const shown = bag ?? unit;
        const price = shown < unit - 0.01
          ? `<span class="text-xs text-muted-foreground line-through">${fmt(unit)}€</span><span class="font-semibold text-primary text-sm">${fmt(shown)}€</span>`
          : `<span class="font-semibold text-primary text-sm">${fmt(unit)}€</span>`;
        const img = i.image ? `<img src="${i.image + (i.image.includes('?') ? '&' : '?') + 'width=160'}" alt="${esc(i.product_title)}" class="w-full h-full object-cover">` : '';
        return `<div class="flex gap-3 p-3 rounded-lg bg-secondary/30">
          <div class="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-muted">${img}</div>
          <div class="flex-1 min-w-0">
            <h4 class="font-medium text-sm truncate text-foreground">${esc(i.product_title)}</h4>
            <div class="flex items-center gap-1.5 mt-0.5">${price}</div>
            <div class="flex items-center gap-1 mt-1.5">
              <button type="button" class="btn btn-outline h-6 w-6" data-line="${i.key}" data-q="${i.quantity - 1}" aria-label="-">${icon('minus')}</button>
              <span class="w-8 text-center text-sm font-medium">${i.quantity}</span>
              <button type="button" class="btn btn-outline h-6 w-6" data-line="${i.key}" data-q="${i.quantity + 1}" aria-label="+">${icon('plus')}</button>
              <button type="button" class="btn btn-ghost h-6 w-6 ml-auto text-muted-foreground hover:text-destructive hover:bg-transparent" data-line="${i.key}" data-q="0" aria-label="Remove">${icon('trash')}</button>
            </div>
          </div>
        </div>`;
      }).join('');

      const [t12, t8, t4] = tiers;
      let upsell = '';
      const row = (title, sub, subCls, btn, tier) => `<div class="flex items-center justify-between gap-3"><div class="min-w-0"><p class="text-sm font-semibold text-foreground">${esc(title)}</p><p class="text-xs ${subCls}">${esc(sub)}</p></div><button type="button" class="btn btn-sm flex-shrink-0 border border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground text-xs" data-upsell data-bags="${tier.qty}" data-name="${esc(tier.name)}">${esc(btn)}</button></div>`;
      if (t4 && count >= 1 && count < t4.qty) upsell = row(S.upsellStarter, S.upsellStarterPrice, 'text-primary font-bold', S.upsellAdd, t4);
      else if (t8 && count >= t4.qty && count < t8.qty) upsell = row(S.upsellUpgrade, S.upsellUpgradeSub, 'text-muted-foreground', S.upsellUpgradeBtn, t8);
      else if (t12 && count >= t8.qty && count < t12.qty) upsell = row(S.upsellValue, S.upsellValueSub, 'text-muted-foreground', S.upsellUpgradeBtn, t12);
      if (upsell) upsell = `<div class="mt-4 border border-dashed border-primary/40 rounded-lg p-3 bg-primary/5">${upsell}</div>`;

      html = `
        <div class="flex-shrink-0 px-6 pt-4 pb-2"><div class="bg-secondary/50 rounded-lg p-3">
          <p class="text-sm font-medium text-center mb-2">${esc(remaining <= 0 ? S.shippingUnlocked : S.shippingAway.replace('{amount}', fmt(remaining)))}</p>
          <div class="relative w-full overflow-hidden rounded-full bg-secondary h-2"><div class="h-full bg-primary transition-all" style="width:${pct}%"></div></div>
        </div></div>
        <div class="flex-1 overflow-y-auto px-6 py-3 min-h-0"><div class="space-y-3">${items}</div>${upsell}</div>
        <div class="flex-shrink-0 border-t border-border bg-background px-6 pb-6 pt-4 space-y-4">
          <div class="flex items-center justify-between text-xs text-muted-foreground">
            <span class="flex items-center gap-1">${icon('lock')}${esc(cartStrings.trustSecure)}</span>
            <span class="flex items-center gap-1">${icon('flag')}${esc(cartStrings.trustMade)}</span>
            <span class="flex items-center gap-1">${icon('truck')}${esc(cartStrings.trustDelivery)}</span>
          </div>
          <div class="flex justify-between items-center"><span class="text-lg font-display">${esc(cartStrings.total)}</span><span class="text-xl font-bold text-primary">${fmt(total)} ${esc(currency)}</span></div>
          <button type="button" class="btn btn-lg w-full bg-primary text-primary-foreground hover:bg-primary/90 text-base font-semibold" data-checkout>${icon('external')}${esc(cartStrings.checkout)}</button>
        </div>`;
    }
    $$('[data-cart-body]').forEach((el) => { el.innerHTML = html; });
  }

  document.addEventListener('click', async (e) => {
    const line = e.target.closest('[data-line]');
    if (line) { line.disabled = true; try { await changeLine(line.dataset.line, Number(line.dataset.q)); } catch (err) { toast(S.error, err.message, true); } return; }

    if (e.target.closest('[data-checkout]')) {
      const t = tierFor(bagCount());
      window.location.href = t && t.code ? `/discount/${encodeURIComponent(t.code)}?redirect=${encodeURIComponent('/checkout')}` : '/checkout';
      return;
    }

    const up = e.target.closest('[data-upsell]');
    if (up) { openBundle(up.dataset.name, Number(up.dataset.bags), true); return; }

    const opener = e.target.closest('[data-cart-open]');
    if (opener && !document.body.matches('.template-cart')) {
      e.preventDefault();
      openOverlay($('#CartDrawer'));
      fetchCart();
      return;
    }

    const add = e.target.closest('[data-add-to-cart]');
    if (add) {
      e.preventDefault();
      e.stopPropagation();
      setBusy(add, true);
      try {
        await addItems([{ id: Number(add.dataset.addToCart), quantity: 1 }]);
        toast(S.addedToCart, add.dataset.title);
      } catch (err) { toast(S.error, err.message, true); }
      setBusy(add, false);
    }
  });

  function setBusy(btn, busy) {
    btn.disabled = busy;
    $('[data-spinner]', btn)?.classList.toggle('hidden', !busy);
    const label = $('[data-label]', btn);
    if (label && !btn.hasAttribute('data-pdp-add')) label.classList.toggle('hidden', busy);
  }

  /* ---------- Bundle picker ---------- */
  const modal = $('#BundleModal');
  let bundle = { name: '', bags: 0, replace: false, sel: {} };

  function openBundle(name, bags, replace = false) {
    if (!modal) return;
    bundle = { name, bags, replace, sel: {} };
    $('[data-bundle-title]', modal).textContent = name;
    renderBundle();
    openOverlay(modal);
  }

  function renderBundle() {
    const used = Object.values(bundle.sel).reduce((a, b) => a + b, 0);
    const left = bundle.bags - used;
    $('[data-bundle-status]', modal).textContent = `${S.choose} ${bundle.bags} ${S.bags} — ${left > 0 ? `${left} ${S.remaining}` : S.allSelected}`;
    $$('[data-bundle-item]', modal).forEach((row) => {
      const q = bundle.sel[row.dataset.bundleItem] || 0;
      $('[data-qty]', row).textContent = q;
      $('[data-step="-1"]', row).disabled = q === 0;
      $('[data-step="1"]', row).disabled = left <= 0;
    });
    const submit = $('[data-bundle-submit]', modal);
    submit.disabled = left > 0;
    $('[data-label]', submit).textContent = left > 0 ? `${S.selectMore} ${left} ${S.more}` : S.bundleAddToCart;
  }

  modal?.addEventListener('click', async (e) => {
    const step = e.target.closest('[data-step]');
    if (step) {
      const id = step.closest('[data-bundle-item]').dataset.bundleItem;
      const used = Object.values(bundle.sel).reduce((a, b) => a + b, 0);
      const cur = bundle.sel[id] || 0;
      const next = Math.max(0, cur + Number(step.dataset.step));
      if (next - cur + used > bundle.bags) return;
      if (next === 0) delete bundle.sel[id]; else bundle.sel[id] = next;
      renderBundle();
      return;
    }
    const submit = e.target.closest('[data-bundle-submit]');
    if (submit) {
      submit.disabled = true;
      $('[data-spinner]', submit).classList.remove('hidden');
      $('[data-cart-icon]', submit).classList.add('hidden');
      try {
        if (bundle.replace) await clearCart();
        await addItems(Object.entries(bundle.sel).map(([id, quantity]) => ({ id: Number(id), quantity })));
        toast(`${bundle.name} ${S.bundleAdded}`);
        closeOverlay(modal);
      } catch (err) { toast(S.error, err.message, true); }
      $('[data-spinner]', submit).classList.add('hidden');
      $('[data-cart-icon]', submit).classList.remove('hidden');
      renderBundle();
    }
  });

  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-bundle-open]');
    if (b) openBundle(b.dataset.name, Number(b.dataset.bags));
  });

  /* ---------- Header: mobile menu + language dropdown ---------- */
  const toggle = $('[data-mobile-toggle]');
  toggle?.addEventListener('click', () => {
    const nav = $('#MobileNav');
    const open = nav.classList.toggle('hidden') === false;
    toggle.setAttribute('aria-expanded', open);
    $('[data-icon-open]', toggle).classList.toggle('hidden', open);
    $('[data-icon-close]', toggle).classList.toggle('hidden', !open);
  });
  const lang = $('[data-lang]');
  if (lang) {
    $('[data-lang-toggle]', lang).addEventListener('click', () => $('[data-lang-menu]', lang).classList.toggle('hidden'));
    document.addEventListener('mousedown', (e) => { if (!lang.contains(e.target)) $('[data-lang-menu]', lang).classList.add('hidden'); });
  }

  /* ---------- Product page: thumbnails + variants ---------- */
  const pdp = $('[data-product-section]');
  if (pdp) {
    pdp.addEventListener('click', (e) => {
      const th = e.target.closest('[data-thumb]');
      if (th) {
        const img = $('[data-main-image] img', pdp);
        if (img) { img.removeAttribute('srcset'); img.src = th.dataset.thumb; }
        $$('[data-thumb]', pdp).forEach((b) => { b.classList.toggle('border-primary', b === th); b.classList.toggle('border-border', b !== th); });
      }
      const v = e.target.closest('[data-variant]');
      if (v) {
        $$('[data-variant]', pdp).forEach((b) => {
          const on = b === v;
          b.classList.toggle('border-primary', on); b.classList.toggle('bg-primary/10', on); b.classList.toggle('text-primary', on);
          b.classList.toggle('border-border', !on); b.classList.toggle('text-muted-foreground', !on);
        });
        $('[data-price]', pdp).textContent = v.dataset.variantPrice;
        const add = $('[data-pdp-add]', pdp);
        add.dataset.addToCart = v.dataset.variant;
        add.disabled = v.dataset.available !== 'true';
        const u = new URL(location.href); u.searchParams.set('variant', v.dataset.variant); history.replaceState(null, '', u);
      }
    });
  }

  /* ---------- Init ---------- */
  fetchCart().catch(() => {});
})();
